<template>
  <div class="widget widget-component" :class="widgetClasses" :style="widgetStyle">
    <component
      :is="widget.componentName"
      ref="component"
      class="widget-component-child"
      v-bind="componentData"
      :widget="widget"
      :container="container"
      :editable="editable"
      :resizing="resizing"
      :cfg-template-edition="cfgTemplateEdition"
      @configChange="updateWidget"
      @setMaxZIndex="setMaxZIndex = !!$event"
      @updateConfigOptions="updateCurrentConfigOptions"
    />
    <div
      v-if="overlay.display && showOverlay"
      ref="overlay"
      class="widget-overlay h-v-centerer"
      @touchend="onTouchEnd"
      @click.stop="onComponentClick"
    >
      <div class="wo-head">
        <div class="wo-h-name h-v-centerer">
          {{ overlay.content }}
        </div>
        <span
          class="wo-delete generic-border generic-border--icon-button generic-border--red"
          @touchend.stop="onTouchEndDelete"
          @click.stop="deleteWidget"
        >
          <Icon>{{ deleteMedium }}</Icon>
        </span>
      </div>
    </div>

    <div class="widget__handle-se ui-resizable-handle ui-resizable-se" />
    <div class="widget__handle-ne ui-resizable-handle ui-resizable-ne" />
    <div class="widget__handle-sw ui-resizable-handle ui-resizable-sw" />
    <div class="widget__handle-nw ui-resizable-handle ui-resizable-nw" />
  </div>
</template>

<script>
/**
 * Main widget holder. Provides draggable and resizable features as well as configuration handlers.
 *
 * @vue-prop {Object} widget -  Required. Widget object as in the DB {x, y, width, height, componentName, componentData}
 * @vue-prop {Boolean} editable - Allow dragging, resizing and editing widget's data
 * @vue-prop {Object} [overlay={display: false}] - Should include a 'display' and 'content' key.
 *                                               content will be the title of the widget overlay
 * @vue-prop {HTMLElement} container - Required if editable. Necesary for draggable and resizable containment
 * @vue-prop {Number} [maxZIndex=1] - ZIndex of the highest elemnt
 * @vue-prop {String} snapSelector - Element selector for jQuery draggable's 'snap' option
 * @vue-prop {Boolean} cfgTemplateEdition - Pass the 'cfgTemplateEdition' param to widgets
 *
 */
import $ from 'jquery';
// Vuex
import { SET_CFG_PAGE_WIDGET, FETCH_CFG_PAGE_WIDGET } from '@/store/action-types';
// Components
import widgets from '@/components/widgets';
import Icon from '@/components/icon.vue';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import SocketMessages from '@/utils/classes/SocketMessages';
import { SColors } from '@/utils/constants';

import { copyMedium, deleteMedium } from '@/assets/icons/';

const NO_PADDING_WIDGETS = ['ButtonLinkWidget', 'PagesSlideshowWidget'];

export default {

  name: 'Widget',

  components: {
    ...widgets,
    Icon,
  },

  mixins: [mixinSocketHandler],

  props: {
    stateWidget: {
      type: Object,
      required: true,
    },
    editable: Boolean,
    overlay: {
      type: Object,
      default: () => ({ display: false }),
    },
    container: {
      type: HTMLElement,
      default: null,
    },
    maxZIndex: {
      type: Number,
      default: 1,
    },
    snapSelector: {
      type: String,
      default: '.widget.widget-component',
    },
    cfgTemplateEdition: Boolean,
  },

  data() {
    return {
      SColors,
      copyMedium,
      deleteMedium,
      widget: {},
      showOverlay: false,
      preventClick: false,
      clickEventHappened: false,
      deleting: false,
      dragStart: null,
      dragOrigin: { x: 0, y: 0 },
      resizing: false,
      // Timeout to prevent repeated refresh requets
      refreshWidgetTimeout: null,
      // If this is true, put this widget above others
      setMaxZIndex: false,
    };
  },

  computed: {
    /** Widget position */
    widgetStyle() {
      const style = {
        position: 'absolute',
        top: `${this.widget.y}%`,
        left: `${this.widget.x}%`,
        zIndex: this.setMaxZIndex ? this.maxZIndex : this.widget.zIndex,
        boxShadow: `0 0 0 1px ${SColors.grayBgHover}`,
      };
      if (this.widget.componentData && this.widget.componentData.designOptions) {
        const opt = this.widget.componentData.designOptions;
        style.padding = `${this.effectivePadding}px`;
        style.boxShadow = opt.shadow === 1 ? `0 0 0 1px ${SColors.grayBgHover}` : 'none';
        style.borderRadius = opt.borderRadius === 1 ? '7px' : '0px';
        style.backgroundColor = opt.bgColor || '#ffffff';
      } else if (this.widget.componentData && this.widget.componentData.bgColor) {
        // Support for widgets created before v0.3.35
        style.backgroundColor = this.widget.componentData.bgColor || '#ffffff';
      }
      style.width = `${this.widget.width}%`;
      style.height = `${this.widget.height}%`;
      return style;
    },
    /** Number: Padding of the widget */
    effectivePadding() {
      let padding = this.isPadded ? 10 : 0;
      const opt = (this.widget.componentData && this.widget.componentData.designOptions) || {};
      padding = isNaN(opt.padding) ? padding : opt.padding;
      return padding;
    },
    /** Widget's component props */
    componentData() {
      return this.widget.componentData instanceof Object ? this.widget.componentData : '';
    },
    widgetClasses() {
      return {
        editable: this.editable,
        nopadding: !this.isPadded,
      };
    },
    isPadded() {
      return !NO_PADDING_WIDGETS.includes(this.widget.componentName);
    },
  },

  watch: {
    // eslint-disable-next-line object-shorthand
    'widget.componentData'(newVal) {
      // Receiving empty componentData from server is transformed into an array
      this.widget.componentData = newVal instanceof Array ? {} : newVal;
    },
    editable(newVal, oldVal) {
      if (oldVal !== newVal) {
        if (newVal === false) {
          $(this.$el).resizable('destroy');
          $(this.$el).draggable('destroy');
        } else {
          this.setupDraggable();
        }
      }
    },
    // eslint-disable-next-line object-shorthand
    'stateWidget.zIndex'(newVal) {
      this.widget.zIndex = newVal;
    },
    stateWidget(newVal) {
      this.widget = $.extend(true, {}, newVal);
      this.$nextTick(this.refresh);
    },
    snapSelector() {
      // Snap option changed, update draggable
      this.setupDraggable();
    },
  },

  mounted() {
    // Setup draggable & resizable
    if (this.editable) {
      this.setupDraggable();
    }
    this.showOverlay = this.overlay.display;
    this.widget = $.extend(true, {}, this.stateWidget);
    this.widget.componentData = this.widget.componentData instanceof Array ? {} : this.widget.componentData;

    // Listen to widget change events
    this.handleSocketMessage(SocketMessages.CC_MSGS.WIDGET, (msg) => {
      if (msg.id === this.widget.id) {
        clearTimeout(this.refreshWidgetTimeout);
        this.refreshWidgetTimeout = setTimeout(() => {
          this.$store.dispatch(FETCH_CFG_PAGE_WIDGET, { id: this.widget.id });
        }, 250);
      }
    });
  },

  beforeDestroy() {
    let inst = $(this.$el).draggable('instance');
    if (inst) {
      $(this.$el).draggable('destroy');
    }
    inst = $(this.$el).resizable('instance');
    if (inst) {
      $(this.$el).resizable('destroy');
    }
  },

  methods: {
    setupDraggable() {
      if (!this.editable) {
        return;
      }

      let paddingDiff = this.effectivePadding * 2;
      $(this.$el).resizable({
        handles: {
          se: '.widget__handle-se',
          sw: '.widget__handle-sw',
          ne: '.widget__handle-ne',
          nw: '.widget__handle-nw',
        },
        containment: this.container,
        snap: this.snapSelector,
        snapTolerance: 7,
        start: () => {
          this.moveOnTop();
          this.resizing = true;
          paddingDiff = this.effectivePadding * 2;
        },
        resize: (e, ui) => {
          this.$refs.component.resize();
          ui.size.width += paddingDiff;
          ui.size.height += paddingDiff;
        },
        stop: (e, ui) => {
          this.widgetUpdated(ui.helper[0]);
          this.$refs.component.sizeChanged();
          this.$emit('dragOrResizeDone');
          this.resizing = false;
        },
      }).on('resize', (e) => {
        e.stopPropagation();
      });

      $(this.$el).draggable({
        addClasses: false,
        containment: this.container,
        snap: this.snapSelector,
        snapTolerance: 7,
        cancel: '.wo-delete',
        start: (e) => {
          this.moveOnTop();
          this.dragOrigin = { x: e.target.offsetLeft, y: e.target.offsetTop };
          this.dragStart = this.$moment();
        },
        stop: (e, ui) => {
          const delay = this.$moment().diff(this.dragStart);
          const distance = Math.max(
            Math.abs(e.target.offsetLeft - this.dragOrigin.x),
            Math.abs(e.target.offsetTop - this.dragOrigin.y),
          );
          if (distance > 10 || delay > 250) {
            this.preventClick = true;
          }
          this.widgetUpdated(ui.helper[0]);
          this.$emit('dragOrResizeDone');
        },
      });
    },
    /** Call refresh on the component */
    refresh() {
      if (this.$refs.component && this.$refs.component.refresh) {
        this.$refs.component.refresh();
      }
    },
    /** Move this widget above the other widgets */
    moveOnTop() {
      this.$emit('moveOnTop', this.widget);
    },
    /** Click event not working because of jquery touch punch */
    onTouchEnd() {
      // Wait for draggable handler to prevent click if necesary
      setTimeout(() => {
        if (this.clickEventHappened) {
          // Click event works while the element hasn't been dragged yet
          this.clickEventHappened = false;
        } else {
          this.onComponentClick();
        }
      }, 1);
    },
    /** Clicking a component should display the config panel on the left */
    onComponentClick(e) {
      if (this.preventClick) {
        this.preventClick = false;
        return;
      }
      if (e) {
        this.clickEventHappened = true;
      }
      this.emitEdition();
    },
    /**
     * Emit the 'edit' event so the page can open up the config panel
     */
    emitEdition() {
      // Widget-related settings available through options.specOptions
      // editionStart is a callback so that widget component have a link to the configPanel
      this.$emit('edit', this, {
        options: {
          specOptions: this.$refs.component.config,
        },
        editionStart: this.$refs.component.mixinConfigEditionStart,
      });
    },
    /** Same as the above to update the options IF the config is already opened for this widget */
    updateCurrentConfigOptions() {
      this.$emit('updateOptions', this, {
        options: { specOptions: this.$refs.component.config },
        editionStart: this.$refs.component.mixinConfigEditionStart,
      });
    },
    /** Call to save the widget after any update */
    widgetUpdated(el) {
      const formerPosition = Object.assign({}, this.widget);
      if (el) {
        const rect = el.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        this.widget.width = el.offsetWidth * 100 / this.container.offsetWidth;
        this.widget.height = el.offsetHeight * 100 / this.container.offsetHeight;
        this.widget.x = (rect.left - containerRect.left) * 100 / this.container.offsetWidth;
        this.widget.y = (rect.top - containerRect.top) * 100 / this.container.offsetHeight;
      }
      // As of v0.3.35, componentData.bgColor should be removed in favor of
      // componentData.designOptions.bgColor
      if (this.widget.componentData) {
        if (this.widget.componentData.bgColor) {
          if (!this.widget.componentData.designOptions) {
            this.widget.componentData.designOptions = {};
          }
          this.widget.componentData.designOptions.bgColor = this.widget.componentData.bgColor;
          delete this.widget.componentData.bgColor;
        }
      }
      this.$store.dispatch(SET_CFG_PAGE_WIDGET, this.widget)
        .then(() => {
          this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.WIDGET, id: this.widget.id });
        })
        .catch(() => {
          const keys = ['x', 'y', 'width', 'height', 'zIndex'];
          keys.forEach((key) => {
            this.widget[key] = formerPosition[key];
          });
        });
    },
    /** Click event disappears after drag or resize */
    onTouchEndDelete() {
      // Timeout to check if click event is working first
      setTimeout(() => {
        // If click event works, don't call it again
        if (this.deleting) {
          this.deleting = false;
        } else {
          this.deleteWidget();
        }
      }, 1);
    },
    /** Prompt the user to confirm the widget removal */
    deleteWidget(e) {
      if (e) {
        this.deleting = true;
      }
      this.$emit('delete');
    },
    /** User change some content in the widget's config */
    configUpdated(option) {
      if (this.widget.componentData == null) {
        this.widget.componentData = {};
      }
      if (option.callback) {
        // Custom callback
        option.callback(option, this.updateWidget);
      } else {
        // Set widget component data
        this.updateWidget(option);
      }
    },
    updateWidget(option) {
      // Any other option
      if (Object.prototype.hasOwnProperty.call(this.widget, option.key)) {
        // Generic widget param
        this.widget[option.key] = option.value;
        this.widgetUpdated();
      } else {
        // Widget-specific param
        this.$set(this.widget.componentData, option.key, option.value);
        this.widgetUpdated();
      }
      this.$nextTick(this.emitEdition);
    },
    /** Window size update */
    sizeChanged() {
      this.$refs.component.sizeChanged();
    },
    /** User data loaded */
    userLoaded() {
      if (this.$refs.component && this.$refs.component.userLoaded) {
        this.$refs.component.userLoaded();
      }
    },
  },
};
</script>

<style>

.widget-component-child {
  height: 100%;
}
</style>
